import { awaitMediaStreamReady, processWaveform } from '../analyser'
import { createWavArrayBuffer } from '../merge'
import { AzureSpeechRecognizer } from './azureSTT'
import { Signal } from './signal'

/** Constants */
const SAMPLE_RATE = 16000

/** Utility to merge buffers into a single Int16Array (as a UInt8Array) */
function mergeBuffers(buffers: Float32Array[]): Uint8Array {
  const totalLength = buffers.reduce((acc, buf) => acc + buf.length, 0)
  const totalBuffer = new Uint8Array(totalLength * 2)
  let offset = 0

  for (const buf of buffers) {
    // Convert Float32 -> Int16 -> Uint8
    const int16View = new Int16Array(buf.map((i) => Math.floor(i * 32767)))
    totalBuffer.set(new Uint8Array(int16View.buffer), offset)
    offset += buf.length * 2
  }

  return totalBuffer
}

type RecognitionResult =
  | { completed: false }
  | {
      completed: true
      data: {
        text: string
        wav: ArrayBuffer | null
      }
    }

export function createAudioTranscriber() {
  let mediaStream: MediaStream | null = null
  let audioCtx: AudioContext | null = null
  let audioWorkletNode: AudioWorkletNode | null = null
  let analyser: AnalyserNode | null = null

  let buffers: Float32Array[] = []
  let waveformData: Float32Array[] = []
  let isRunning = false

  // Interval references
  let waveformInterval: NodeJS.Timer | null = null

  // For timeouts
  let recordTimeoutId: ReturnType<typeof setTimeout> | null = null

  let azure: Awaited<ReturnType<typeof AzureSpeechRecognizer>> | null = null

  let finishSignal: Signal<RecognitionResult> | null = null

  /**
   * Cleanup any resources (tracks, context, intervals).
   * This can be called internally after complete or externally.
   */
  async function cleanup() {
    // Stop the analyzer update
    if (waveformInterval) {
      clearInterval(waveformInterval)
      waveformInterval = null
    }

    // Stop the media tracks
    if (mediaStream) {
      mediaStream.getTracks().forEach((track) => track.stop())
      mediaStream = null
    }

    // Disconnect AudioNodes
    if (audioWorkletNode) {
      audioWorkletNode.port.onmessage = null
      audioWorkletNode.disconnect()
      audioWorkletNode = null
    }
    if (analyser) {
      analyser.disconnect()
      analyser = null
    }
    if (audioCtx) {
      try {
        await audioCtx.close()
      } catch (err) {
        console.error('Error closing audio context', err)
      }
      audioCtx = null
    }

    // Reset any session type if needed
    if ((navigator as any).audioSession) {
      ;(navigator as any).audioSession.type = 'playback'
    }

    // Clear the rest
    buffers = []
    waveformData = []
    azure = null
    isRunning = false

    // Clear any pending timeout
    if (recordTimeoutId) {
      clearTimeout(recordTimeoutId)
      recordTimeoutId = null
    }

    if (finishSignal) {
      finishSignal.resolve({ completed: false })
      finishSignal = null
    }
  }

  /**
   * Internal: Start the microphone, create context and nodes, begin collecting data.
   * Returns a cleanup function (which will be invoked automatically or manually).
   */
  async function startRecording(
    onChunk: (chunk: Float32Array) => void,
    onWaveformData?: (data: number[]) => void
  ): Promise<{ stop: () => Promise<void> }> {
    try {
      if (mediaStream) {
        throw new Error('Recording already in progress.')
      }

      // Update audio session type (if your platform needs it)
      if ((navigator as any).audioSession) {
        ;(navigator as any).audioSession.type = 'play-and-record'
      }

      // 1. Get user media
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleSize: 16,
          channelCount: 1,
          echoCancellation: false,
          noiseSuppression: true,
        },
        video: false,
      })

      const devices = await navigator.mediaDevices.enumerateDevices()
      const audioDevices = devices.filter((d) => d.kind === 'audioinput')
      const tracks = mediaStream.getTracks()
      if (audioDevices.length > 1) {
        console.warn('[Transcriber] ManyDevicesWarning: More than one audio device')
      }
      console.log('[Transcriber] Selected audio device:', tracks[0].label)
      console.log('[Transcriber] Audio devices:', audioDevices.map((d) => d.label).join(', '))

      // 2. Ensure stream is active
      await new Promise<void>((res) => {
        const checkActive = setInterval(() => {
          if (
            mediaStream &&
            mediaStream.active &&
            mediaStream.getTracks().length &&
            mediaStream.getTracks()[0].readyState === 'live'
          ) {
            clearInterval(checkActive)
            res()
          }
        }, 10)
      })
      // 3. Create AudioContext and nodes
      audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({
        sampleRate: SAMPLE_RATE,
      })
      const source = audioCtx.createMediaStreamSource(mediaStream)

      analyser = audioCtx.createAnalyser()
      analyser.fftSize = 64
      analyser.smoothingTimeConstant = 0.8
      source.connect(analyser)

      // Wait for the analysis node to be ready
      await awaitMediaStreamReady(analyser)

      // Init waveform data if needed
      if (onWaveformData) {
        onWaveformData(processWaveform(analyser))
        waveformInterval = setInterval(() => {
          onWaveformData(processWaveform(analyser))
        }, 50)
      }

      // 4. Load audio worklet processor
      await audioCtx.audioWorklet.addModule('/audio-processor-4.js')
      audioWorkletNode = new AudioWorkletNode(audioCtx, 'recorder-processor')

      // 5. On each chunk from the audio worklet, push to onChunk
      audioWorkletNode.port.onmessage = (event) => {
        if (event.data?.type === 'data') {
          const buf: Float32Array = event.data.buffer
          onChunk(buf)
        }
      }

      source.connect(audioWorkletNode)

      // Return cleanup function
      return {
        stop: async () => {
          // Wait a little to ensure last chunk is processed
          await new Promise((r) => setTimeout(r, (6400 / SAMPLE_RATE) * 1000 + 200))
          // Cleanup
          await cleanup()
        },
      }
    } catch (error) {
      await cleanup()
      throw error
    }
  }

  /**
   * Starts recognition (both audio capture and Azure speech).
   * @param options Various callbacks and config
   */
  async function startRecognition(options?: {
    onWaveformData?: (wv: number[]) => void
    onTranscription?: (text: string) => void
    timeoutMs?: number // Timeout to abort automatically
    onStarted?: () => void
    onEnded?: () => void
  }): Promise<[Promise<void>, Promise<RecognitionResult>]> {
    try {
      if (isRunning) {
        console.warn('Recognition is already running.')
        return [Promise.resolve(), Promise.resolve({ completed: false })]
      }
      isRunning = true
      const { onWaveformData, onTranscription, timeoutMs = 300000, onStarted, onEnded } = options || {}
      buffers = []
      let bufferedFrames = 0

      await startRecording((chunk) => {
        buffers.push(chunk)
        waveformData.push(chunk)
        bufferedFrames += chunk.length

        if (azure?.pushStream && bufferedFrames >= 1600) {
          azure?.pushStream.writes(buffers).catch((err: any) => console.error('PushStream write error:', err))
          buffers = []
          bufferedFrames = 0
        }
      }, onWaveformData)

      azure = await AzureSpeechRecognizer({
        setTranscription: (t) => {
          if (onTranscription) onTranscription(t)
        },
      })

      onStarted?.()

      if (timeoutMs > 0) {
        recordTimeoutId = setTimeout(() => {
          console.log('Recognition timed out.')
          stopRecognition(true).catch((err) => console.error('Stop on timeout failed', err))
        }, timeoutMs)
      }

      finishSignal = new Signal<RecognitionResult>()
      finishSignal.promise.then(onEnded)
      return [Promise.resolve(), finishSignal.promise]
    } catch (error) {
      await stopRecognition(false)
      throw error
    }
  }

  async function stopRecognition(complete: boolean): Promise<[string, ArrayBuffer | null]> {
    let text = ''
    let wav: ArrayBuffer | null = null
    try {
      if (!isRunning) {
        return [text, wav]
      }

      isRunning = false

      if (azure) {
        text = await azure.destroy()
      }

      if (complete && waveformData.length) {
        const merged = mergeBuffers(waveformData)
        wav = createWavArrayBuffer(merged, SAMPLE_RATE)
      }

      buffers = []

      finishSignal?.resolve({
        completed: complete,
        data: {
          text,
          wav,
        },
      })
      return [text, wav]
    } catch (e) {
      finishSignal?.reject(e)
      throw e
    } finally {
      await cleanup()
    }
  }

  // Optional helper to see if we're currently recording
  function isRecording() {
    return isRunning
  }

  // Return the public API
  return {
    startRecognition,
    stopRecognition,
    isRecording,
  }
}
